import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { PeriodRow } from "../types";
import {
  getBankingContractBlocks,
  getBankingContractReturnBlocks,
  getBankingCumulativeBlocks,
  getBankingCumulativeReturnBlocks,
} from "../../../../Rest_api/RestApi";
import { CUMULATIVE_ID } from "./ContractsSidebar";
import { BankingContractAnalyticsBucket } from "../../../../models/banking";
import { formatBucketLabel } from "../helpers";
import { Granularity } from "../types";

function groupBlocks(
  blocks: BankingContractAnalyticsBucket[],
): BankingContractAnalyticsBucket[] {
  const grouped = new Map<number, BankingContractAnalyticsBucket>();
  for (const b of blocks) {
    const blockNum = b.time_block ?? 0;
    const existing = grouped.get(blockNum);
    if (existing) {
      existing.scheduled_mwh =
        (existing.scheduled_mwh ?? 0) + (b.scheduled_mwh ?? 0);
      existing.actual_mwh = (existing.actual_mwh ?? 0) + (b.actual_mwh ?? 0);
      if (b.approval_breakdown && b.approval_breakdown.length > 0) {
        existing.approval_breakdown = [
          ...(existing.approval_breakdown ?? []),
          ...b.approval_breakdown,
        ];
      }
    } else {
      grouped.set(blockNum, { ...b });
    }
  }
  return Array.from(grouped.values()).sort((a, b) => {
    if (
      a.time_block !== undefined &&
      b.time_block !== undefined &&
      a.time_block !== b.time_block
    ) {
      return a.time_block - b.time_block;
    }
    const tA = a.time || extractBlockTime(a.date);
    const tB = b.time || extractBlockTime(b.date);
    return tA.localeCompare(tB);
  });
}

function tradersInBlocks(
  blocks: BankingContractAnalyticsBucket[],
): string[] {
  const seen = new Set<string>();
  for (const b of blocks) {
    const parts = b.approval_breakdown ?? [];
    for (const p of parts) {
      if (p.trader) seen.add(p.trader);
    }
  }
  return Array.from(seen);
}

function extractBlockTime(raw: string): string {
  if (!raw) return "";
  const tIdx = raw.indexOf("T");
  if (tIdx !== -1) {
    const timePart = raw.slice(tIdx + 1, tIdx + 6);
    if (timePart.includes(":")) return timePart;
  }
  const spaceIdx = raw.indexOf(" ");
  if (spaceIdx !== -1) {
    const timePart = raw.slice(spaceIdx + 1, spaceIdx + 6);
    if (timePart.includes(":")) return timePart;
  }
  if (raw.includes(":") && raw.length <= 8) return raw.slice(0, 5);
  return "";
}

function DeltaPill({ value, suffix = "" }: { value: number; suffix?: string }) {
  const neg = value < 0;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums ${
        neg ? "bg-[#FEF2F2] text-[#E0463C]" : "bg-[#F0FDF4] text-[#1F8A39]"
      }`}
    >
      {value >= 0 ? "+" : ""}
      {value.toFixed(suffix === "%" ? 1 : 2)}
      {suffix}
    </span>
  );
}

export function PeriodBreakdownTable({
  rows,
  contractId,
  enableExpand = true,
  mode = "supply",
  title = "Period breakdown",
  preloadedBlocks,
  granularity = "Day",
  showApprovalNos: showApprovalNosProp,
  traderName,
}: {
  rows: PeriodRow[];
  contractId: string;
  enableExpand?: boolean;
  mode?: "supply" | "return";
  title?: string;
  preloadedBlocks?: Record<string, BankingContractAnalyticsBucket[]>;
  granularity?: Granularity;
  showApprovalNos?: boolean;
  traderName?: string;
}) {
  const hasApprovalNos =
    showApprovalNosProp ?? rows.some((r) => (r.approval_nos ?? []).length > 0);
  const hasTraderName = !!traderName;
  const totalCols = 6 + (hasApprovalNos ? 1 : 0) + (hasTraderName ? 1 : 0);

  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [blockCache, setBlockCache] = useState<
    Record<string, BankingContractAnalyticsBucket[]>
  >(() => {
    if (!preloadedBlocks) return {};
    const processed: Record<string, BankingContractAnalyticsBucket[]> = {};
    Object.entries(preloadedBlocks).forEach(([date, blocks]) => {
      processed[date] = groupBlocks(blocks);
    });
    return processed;
  });

  useEffect(() => {
    if (!preloadedBlocks) {
      setBlockCache({});
    } else {
      const processed: Record<string, BankingContractAnalyticsBucket[]> = {};
      Object.entries(preloadedBlocks).forEach(([date, blocks]) => {
        processed[date] = groupBlocks(blocks);
      });
      setBlockCache(processed);
    }
    setExpandedDate(null);
  }, [preloadedBlocks, granularity]);

  const [loadingDate, setLoadingDate] = useState<string | null>(null);
  const [errorDate, setErrorDate] = useState<{
    date: string;
    message: string;
  } | null>(null);

  const toggleExpand = async (date: string) => {
    if (expandedDate === date) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(date);
    setErrorDate(null);
    if (date in blockCache) return;
    if (!contractId) return;
    setLoadingDate(date);
    try {
      let data: BankingContractAnalyticsBucket[] = [];
      if (contractId === CUMULATIVE_ID) {
        data = await (mode === "return"
          ? getBankingCumulativeReturnBlocks()
          : getBankingCumulativeBlocks());
      } else {
        data = await (mode === "return"
          ? getBankingContractReturnBlocks(contractId)
          : getBankingContractBlocks(contractId));
      }

      const byLabel: Record<string, BankingContractAnalyticsBucket[]> = {};
      for (const b of data) {
        const label = formatBucketLabel(b.date, granularity);
        if (!byLabel[label]) byLabel[label] = [];
        byLabel[label].push(b);
      }

      setBlockCache((prev) => {
        const next: Record<string, BankingContractAnalyticsBucket[]> = {
          ...prev,
        };
        for (const [label, blocks] of Object.entries(byLabel)) {
          next[label] = groupBlocks(blocks);
        }
        if (!(date in next)) next[date] = [];
        return next;
      });
    } catch (err) {
      setErrorDate({
        date,
        message: (err as Error)?.message ?? "Failed to load blocks",
      });
    } finally {
      setLoadingDate((curr) => (curr === date ? null : curr));
    }
  };

  return (
    <div className="bg-white border border-[#ECEEF3] rounded-[10px] overflow-hidden mb-3.5">
      {/* Card header */}
      <div className="px-4 py-3 border-b border-[#ECEEF3] flex items-center justify-between">
        <div>
          <div className="text-[13px] font-semibold text-[#1A1B2B]">{title}</div>
          {enableExpand && (
            <div className="text-[11px] text-[#9B96A8] mt-0.5">
              Click a row to drill into 15-minute blocks
            </div>
          )}
        </div>
        <span className="text-[11px] font-medium text-[#8C8A96] bg-[#F4F5F8] px-2.5 py-0.5 rounded-full">
          {rows.length} {rows.length === 1 ? "record" : "records"}
        </span>
      </div>

      <div className="overflow-x-auto overflow-y-auto max-h-[480px]">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-[#F8F9FB]">
              <Th align="left" minW="min-w-[110px]">DATE</Th>
              <Th align="right" minW="min-w-[110px]">
                {granularity === "Time Block" ? "SCHEDULED MW" : "SCHEDULED MWH"}
              </Th>
              <Th align="right" minW="min-w-[100px]">
                {granularity === "Time Block" ? "ACTUAL MW" : "ACTUAL MWH"}
              </Th>
              <Th align="right" minW="min-w-[80px]">Δ MWH</Th>
              <Th align="right" minW="min-w-[60px]">Δ %</Th>
              <Th align="left" minW="min-w-[120px]">CATEGORY</Th>
              {hasApprovalNos && (
                <Th align="left" minW="min-w-[160px]">APPROVAL NOs.</Th>
              )}
              {hasTraderName && (
                <Th align="left" minW="min-w-[110px]">TRADER NAME</Th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, rowIdx) => {
              const isOpen = enableExpand && expandedDate === r.date;
              const isLoading = enableExpand && loadingDate === r.date;
              const blocks = blockCache[r.date] ?? [];
              const isEven = rowIdx % 2 === 0;
              const rowTraders = tradersInBlocks(blocks);
              const rowTraderName =
                rowTraders.length > 0 ? rowTraders.join(", ") : traderName;

              return (
                <Fragment key={r.date}>
                  <tr
                    onClick={
                      enableExpand ? () => toggleExpand(r.date) : undefined
                    }
                    className={[
                      enableExpand ? "cursor-pointer" : "",
                      isOpen
                        ? "bg-[#FFF8F5]"
                        : isEven
                        ? "bg-white hover:bg-[#FAFBFD]"
                        : "bg-[#FAFBFD] hover:bg-[#F4F5F8]",
                      "transition-colors",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {/* Date */}
                    <td className="text-left px-3.5 py-2.5 border-b border-[#F0F1F5]">
                      <span className="inline-flex items-center gap-1.5">
                        {enableExpand &&
                          (isLoading ? (
                            <Loader2
                              size={12}
                              className="text-[#828A99] animate-spin shrink-0"
                            />
                          ) : isOpen ? (
                            <ChevronDown
                              size={12}
                              className="text-[#E2682B] shrink-0"
                            />
                          ) : (
                            <ChevronRight
                              size={12}
                              className="text-[#C8C6CF] shrink-0"
                            />
                          ))}
                        <span className="font-medium text-[#1A1B2B] tabular-nums">
                          {r.label || formatBucketLabel(r.date, granularity)}
                        </span>
                      </span>
                    </td>

                    <td className="text-right text-[#1A1B2B] font-medium px-3.5 py-2.5 border-b border-[#F0F1F5] tabular-nums">
                      {r.scheduled.toFixed(2)}
                    </td>
                    <td className="text-right text-[#1A1B2B] font-medium px-3.5 py-2.5 border-b border-[#F0F1F5] tabular-nums">
                      {r.actual.toFixed(2)}
                    </td>
                    <td className="text-right px-3.5 py-2.5 border-b border-[#F0F1F5]">
                      <DeltaPill value={r.delta} />
                    </td>
                    <td className="text-right px-3.5 py-2.5 border-b border-[#F0F1F5]">
                      <DeltaPill value={r.pct} suffix="%" />
                    </td>

                    {/* Category */}
                    <td className="text-left px-3.5 py-2.5 border-b border-[#F0F1F5]">
                      <CategoryText items={r.categories ?? []} />
                    </td>

                    {hasApprovalNos && (
                      <td className="text-left px-3.5 py-2.5 border-b border-[#F0F1F5]">
                        <ApprovalNos items={r.approval_nos ?? []} />
                      </td>
                    )}

                    {hasTraderName && (
                      <td className="text-left text-[#525062] font-medium px-3.5 py-2.5 border-b border-[#F0F1F5]">
                        {rowTraderName}
                      </td>
                    )}
                  </tr>

                  {/* Error row */}
                  {isOpen && errorDate?.date === r.date && (
                    <tr className="bg-[#FFF5F5]">
                      <td
                        colSpan={totalCols}
                        className="px-3.5 py-2 text-[11px] text-[#E0463C] border-b border-[#F0F1F5]"
                      >
                        {errorDate.message}
                      </td>
                    </tr>
                  )}

                  {/* Empty blocks row */}
                  {isOpen && !isLoading && !errorDate && blocks.length === 0 && (
                    <tr className="bg-[#FAFBFD]">
                      <td
                        colSpan={totalCols}
                        className="px-3.5 py-2 text-[11px] text-[#828A99] border-b border-[#F0F1F5]"
                      >
                        No 15-minute block data for this date.
                      </td>
                    </tr>
                  )}

                  {/* Expanded block rows */}
                  {isOpen &&
                    [...blocks]
                      .sort(
                        (a, b) => (a.time_block ?? 0) - (b.time_block ?? 0),
                      )
                      .map((b, idx) => {
                        const parts = b.approval_breakdown ?? [];
                        const isSplit = parts.length >= 2;
                        const timeLabel =
                          b.time ?? extractBlockTime(b.date) ?? b.date;
                        const blockNumber = b.time_block ?? idx + 1;
                        // Zebra background per block so each block is
                        // visually separated from the next one.
                        const blockBg =
                          idx % 2 === 0 ? "bg-white" : "bg-[#F6F7FA]";
                        const blockHover =
                          idx % 2 === 0
                            ? "hover:bg-[#FAFBFD]"
                            : "hover:bg-[#EFF1F5]";

                        if (isSplit) {
                          return (
                            <Fragment key={`${r.date}-${b.time_block ?? idx}`}>
                              {parts.map((p, i) => {
                                const isFirst = i === 0;
                                const isLast = i === parts.length - 1;
                                const cellBase = "py-2";
                                // Columns the mid-block divider spans: the
                                // value area to the right of the slot cell,
                                // now including the trader column since each
                                // approval line carries its own trader.
                                const midDividerCols = totalCols - 1;
                                return (
                                  <Fragment key={`${p.approval_no}-${i}`}>
                                    <tr className={`${blockBg} ${blockHover} transition-colors`}>
                                      {/* Block number/time — once, vertically centered across the block's sub-rows */}
                                      {isFirst ? (
                                        <td
                                          rowSpan={parts.length * 2 - 1}
                                          className="text-left pl-9 pr-3.5 align-middle py-2 border-l-2 border-[#D3D7E0]"
                                        >
                                          <span className="inline-flex items-center gap-2">
                                            <span className="inline-flex items-center justify-center min-w-[26px] h-[18px] px-1.5 rounded-md bg-[#ECEEF3] text-[#525062] font-semibold text-[10px]">
                                              {blockNumber}
                                            </span>
                                            <span className="text-[#828A99] text-[11px] tabular-nums">
                                              {timeLabel || "--:--"}
                                            </span>
                                          </span>
                                        </td>
                                      ) : null}

                                      <td
                                        className={`${cellBase} pl-3.5 pr-3.5 text-right tabular-nums text-[11px] text-[#525062]`}
                                      >
                                        {p.scheduled_mwh.toFixed(2)}
                                      </td>
                                      <td
                                        className={`${cellBase} px-3.5 text-right tabular-nums text-[11px] text-[#525062]`}
                                      >
                                        {p.actual_mwh.toFixed(2)}
                                      </td>
                                      <td className={`${cellBase} px-3.5 text-right`}>
                                        <DeltaPill value={p.delta_mwh} />
                                      </td>
                                      <td className={`${cellBase} px-3.5 text-right`}>
                                        <DeltaPill value={p.delta_pct} suffix="%" />
                                      </td>
                                      <td
                                        className={`${cellBase} px-3.5 text-left text-[11px] text-[#525062]`}
                                      >
                                        {p.category || "—"}
                                      </td>
                                      {hasApprovalNos && (
                                        <td
                                          className={`${cellBase} px-3.5 text-left text-[10px] leading-tight tracking-tight text-[#525062]`}
                                        >
                                          {p.approval_no}
                                        </td>
                                      )}
                                      {hasTraderName && (
                                        <td
                                          className={`${cellBase} px-3.5 text-left text-[11px] text-[#525062]`}
                                        >
                                          {p.trader ?? (isFirst ? traderName : "")}
                                        </td>
                                      )}
                                    </tr>
                                    {/* Divider line between the approval values (spans trader column too) */}
                                    {!isLast && (
                                      <tr aria-hidden="true">
                                        <td
                                          colSpan={midDividerCols}
                                          className="p-0"
                                        >
                                          <div className="h-px bg-[#D3D7E0] mx-3.5" />
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                              
                           
                            </Fragment>
                          );
                        }

                        return (
                          <tr
                            key={`${r.date}-${b.time_block ?? idx}`}
                            className={`${blockBg} ${blockHover} transition-colors`}
                          >
                            <td className="text-left pl-9 pr-3.5 py-2 border-b border-[#ECEEF3] border-l-2 border-l-[#D3D7E0]">
                              <span className="inline-flex items-center gap-2">
                                <span className="inline-flex items-center justify-center min-w-[26px] h-[18px] px-1.5 rounded-md bg-[#ECEEF3] text-[#525062] font-semibold text-[10px]">
                                  {blockNumber}
                                </span>
                                <span className="text-[#828A99] text-[11px] tabular-nums">
                                  {timeLabel || "--:--"}
                                </span>
                              </span>
                            </td>
                            <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#ECEEF3] tabular-nums text-[11px]">
                              {b.scheduled_mwh.toFixed(2)}
                            </td>
                            <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#ECEEF3] tabular-nums text-[11px]">
                              {b.actual_mwh.toFixed(2)}
                            </td>
                            <td className="text-right px-3.5 py-2 border-b border-[#ECEEF3]">
                              <DeltaPill value={b.delta_mwh} />
                            </td>
                            <td className="text-right px-3.5 py-2 border-b border-[#ECEEF3]">
                              <DeltaPill value={b.delta_pct} suffix="%" />
                            </td>
                            <td className="text-left px-3.5 py-2 border-b border-[#ECEEF3] text-[11px]">
                              <CategoryText items={b.categories ?? []} small />
                            </td>
                            {hasApprovalNos && (
                              <td className="text-left px-3.5 py-2 border-b border-[#ECEEF3]">
                                <ApprovalNos
                                  items={b.approval_nos ?? []}
                                  small
                                />
                              </td>
                            )}
                            {hasTraderName && (
                              <td className="text-left text-[#525062] px-3.5 py-2 border-b border-[#ECEEF3] text-[11px]">
                                {parts[0]?.trader ?? traderName}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryText({
  items,
  small = false,
}: {
  items: string[];
  small?: boolean;
}) {
  if (items.length === 0) {
    return <span className="text-[#C8C6CF]">—</span>;
  }
  return (
    <span
      className={`text-[#525062] leading-tight ${
        small ? "text-[10px]" : "text-[11px]"
      }`}
    >
      {items.join(", ")}
    </span>
  );
}

function ApprovalNos({
  items,
  small = false,
}: {
  items: string[];
  small?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <div
      className={`flex flex-col gap-0.5 ${
        small ? "text-[10px]" : "text-[11px]"
      }`}
    >
      {items.map((a) => (
        <span
          key={a}
          className="text-[#525062] leading-tight tracking-tight"
        >
          {a}
        </span>
      ))}
    </div>
  );
}

function Th({
  children,
  align,
  minW,
}: {
  children: React.ReactNode;
  align: "left" | "right" | "center";
  minW?: string;
}) {
  return (
    <th
      className={[
        align === "left" ? "text-left" : "text-right",
        "sticky top-0 z-10 bg-[#F8F9FB] font-semibold text-[10px] tracking-wider text-[#8C8A96] px-3.5 py-2.5 border-b border-[#E4E6ED] whitespace-nowrap",
        minW ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </th>
  );
}

export default PeriodBreakdownTable;
